<template>
  <div v-if="isOpen" class="pp-root" data-test="schedule-patient-panel-root">
    <div class="pp-backdrop" data-test="pp-backdrop" @click="close"></div>

    <aside
      ref="panel"
      class="pp-panel"
      data-test="schedule-patient-panel"
      role="dialog"
      aria-modal="true"
      :aria-label="`Patient: ${panelName}`"
      tabindex="-1"
    >
      <div class="pp-scroll">
        <!-- Phone only (hidden by CSS elsewhere): the panel is full screen, so it needs a way back. -->
        <div class="pp-bar">
          <button type="button" class="pp-bar__btn" aria-label="Back to the schedule" data-test="pp-back" @click="close">
            <i class="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <span class="pp-bar__t">Patient</span>
          <span class="pp-bar__btn" aria-hidden="true"></span>
        </div>

        <PatientProfileView
          mode="panel"
          :payload="payload"
          :data-loading="dataLoading"
          :data-error="dataError"
          :unavailable="unavailable"
          :appointment="appointment"
          :appointment-meta="appointmentMeta"
          :insurance="insurance"
          :insurance-loading="insuranceLoading"
          :signed-plans="signedPlans"
          :signed-plans-loading="signedPlansLoading"
          :tasks="tasks"
          :employees="employees"
          :followups="panelFollowups"
          :tiles="tiles"
          :alert-details="alertDetails"
          :today="today || ''"
          :date-format="dateFormat"
          :timezone="timezone"
          :capabilities="capabilities"
          :tab="tab"
          :profile-link="profileLink"
          @tab="setTab"
          @retry="retry"
          @close="close"
        >
          <template #actions>
            <PatientPanelActions v-if="patient" :actions="actionList" @action="onAction" />
          </template>
        </PatientProfileView>
      </div>
    </aside>

    <!--
      Runs the actions (the redesigned action panels, and the Tx Presenter overlays). OUTSIDE the
      <aside> on purpose: the aside is a stacking context at 1035, and anything rendered inside it
      (the Tx Presenter overlays) could never rise above it — see PatientPanelActionHost.vue.
    -->
    <PatientPanelActionHost
      v-if="patient"
      ref="actionHost"
      :patient-data="patientData"
      :patient-num="openPatNum"
      :site-id="panelSiteID"
      :gates="actionGates"
      :signed-plans="signedPlans || []"
      :followup-context="followupContext"
      @busy="onBusy"
      @followup-saved="retry"
      @open-tab="setTab"
    />
  </div>
</template>

<script>
/**
 * The Patient Panel — one patient, in a slide-over, without leaving the page (Schedule View Phase 7,
 * design "Layout 1A", `layout: 'tm'`).
 *
 * Plan: docs/plans/2026-09-19-schedule-view-plan.md · state: src/store/patientPanel.js
 *
 * The tabs are read-only (Phase 7). The action bar (Phase 8) — Send form/text/email, Tx plans,
 * Create task, Review, Photo to OD, Form QR, Payment — REUSES Patient Profile's own logic (the redesigned
 * panels `extends` its components) with Patient Profile's own gates: PatientPanelActions.vue draws it, PatientPanelActionHost.vue runs it,
 * src/utils/patientPanel/actionGates.js decides what is enabled (agreement-tested against
 * PatientProfile.vue). Follow-up — the tab and the "Start a follow-up" button — lights up when
 * /schedule-view/patient carries the follow-up rows (Phase 6, 2026-10-08); an older stack without them
 * leaves both disabled.
 *
 * Patient Profile overhaul (2026-10-08): everything inside the slide-over — header, Needs attention,
 * tabs — is PatientProfileView.vue, which the new Patient Profile PAGE draws too. This file is the
 * shell: backdrop, <aside>, phone back bar, Escape, store wiring, the action bar and its host.
 *
 * What the HOST passes in (all live, so the panel follows the schedule while it is open):
 * the appointment, its resolved chair/provider/status, the day's alert payload and the office's
 * alert settings. Everything about the patient itself is fetched by the store. Today the host is
 * ScheduleView.vue; the plan moves it to Layout.vue for the header patient search.
 *
 * ⛔ z-index 1035: above the app header (1030) but BELOW Bootstrap's modal layer (1040/1050), so the
 * modal the actions open (Collect Payment) appears on top of it. What draws LOWER is lifted
 * to 1036: the $showPanel slide-outs (the action panels, ~101) by the host page's
 * outlet wrapper (ScheduleView.vue .sv-slideout-host — the host page must mount exactly ONE
 * <slideout-panel>), and the Tx Presenter overlays (100) by PatientPanelActionHost's wrapper. The
 * AI agent's floating button (9999) still floats over the panel's corner; that is the trade, and
 * raising the panel above it would bury every modal it opens.
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import { mapState, mapGetters } from 'vuex';
import PatientProfileView from './PatientProfileView.vue';
import PatientPanelActions from './PatientPanelActions.vue';
import PatientPanelActionHost from './PatientPanelActionHost.vue';
import { patientFullName, profilePatientData } from '@/utils/patientPanel/format';
import { resolveActionGates, buildActions, keySettings } from '@/utils/patientPanel/actionGates';
import { maskState } from '@/utils/phiMaskState';
import { scheduleSendCatalogPrefetch } from '@/utils/sendPanelCatalog';
import { blockAlertKeys, alertsForPatient, describeAlert } from '@/utils/scheduleView/alerts';
import { ALERT_KEYS, ALERT_META } from '@/utils/scheduleView/settingsForm';

/** What can be open on top of the panel (see onKeydown). */
const OVERLAYS_ABOVE = [
  '.modal.show',                  // Bootstrap modals: Collect Payment
  '.slideout-panel .slideout',    // the action panels: Follow-up, Form, Text, Email, Task, Review, Photo, QR
  '.ptpm-backdrop', '.spm-backdrop', // Tx Presenter: pick a plan, send it
  '.popover.pp-why-popover',      // a disabled action's reason
].join(', ');

const META_BY_KEY = {};
ALERT_META.forEach((m) => { META_BY_KEY[m.key] = m; });

export default {
  name: 'PatientPanel',

  components: { PatientProfileView, PatientPanelActions, PatientPanelActionHost },

  props: {
    /** The live appointment the panel was opened from, or null. */
    appointment: { type: Object, default: null },
    /** { opName, providerName, bucket, statusText, broken } for that appointment. */
    appointmentMeta: { type: Object, default: null },
    /** The host's /schedule-view/alerts payload (live), or null. */
    alertPayload: { type: Object, default: null },
    /** The office's resolved Schedule View settings — which alerts are switched on. */
    viewSettings: { type: Object, default: null },
    /** The site's IANA timezone, for instants such as a plan's signed-at time. */
    timezone: { type: String, default: '' },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
  },

  data() {
    return { busy: { txplans: false, pay: false } };
  },

  computed: {
    ...mapState('patientPanel', {
      isOpen: s => s.isOpen,
      openPatNum: s => s.openPatNum,
      today: s => s.today,
      tab: s => s.tab,
      capabilities: s => s.capabilities,
      payload: s => s.data,
      dataLoading: s => s.dataLoading,
      dataError: s => s.dataError,
      unavailable: s => s.unavailable,
      insurance: s => s.insurance,
      insuranceLoading: s => s.insuranceLoading,
      signedPlans: s => s.signedPlans,
      signedPlansLoading: s => s.signedPlansLoading,
    }),
    ...mapGetters('patientPanel', ['tasks']),

    employees() {
      return (this.$store.state.customer && this.$store.state.customer.employees) || [];
    },

    /** Named panelSiteID, not siteID — mixin.js defines a `siteID` computed. */
    panelSiteID() {
      return (this.$store.state.customer && this.$store.state.customer.siteID) || '';
    },

    patient() {
      return (this.payload && this.payload.patient) || null;
    },

    /**
     * The follow-up rows (Phase 6, 2026-10-08), or null when /schedule-view/patient does not send them
     * — an older stack during a deploy. null keeps the Follow-ups tab and button disabled.
     */
    panelFollowups() {
      return this.payload && Array.isArray(this.payload.followups) ? this.payload.followups : null;
    },

    /** What the action host's "Start a follow-up" panel needs. null while the rows are missing (an older stack). */
    followupContext() {
      if (!this.patient || !this.panelFollowups) return null;
      return {
        patient: this.patient,
        followups: this.panelFollowups,
        unscheduledTx: (this.payload && this.payload.unscheduledTx) || [],
        lists: (this.payload && this.payload.lists) || [],
        today: this.today || '',
      };
    },

    panelName() {
      const preferred = this.appointment && this.appointment.patient && this.appointment.patient.Preferred;
      return patientFullName(this.patient || (this.appointment && this.appointment.patient), preferred);
    },

    profileLink() {
      if (!this.panelSiteID || !this.openPatNum) return '';
      return `/app/patientprofile/${this.panelSiteID}-${this.openPatNum}`;
    },

    /**
     * The alerts lit for this patient — the block's exact rule: computed by the server (`ran`,
     * not failed) AND switched on by the office. ⛔ The block's own FUNCTION, not a copy of its
     * rule (`blockAlertKeys`, alerts.js): the block, the checklist chip, the checklist card and
     * this panel all call it, so none of them can drift from the others by one filter.
     */
    visibleKeys() {
      if (!this.alertPayload || !this.openPatNum) return [];
      return blockAlertKeys(this.alertPayload, this.openPatNum, this.viewSettings, ALERT_KEYS);
    },

    /** Details for the visible alerts only — a switched-off alert must not leak into a tab. */
    alertDetails() {
      const all = alertsForPatient(this.alertPayload, this.openPatNum);
      const out = {};
      this.visibleKeys.forEach((k) => { out[k] = all[k]; });
      return out;
    },

    tiles() {
      return this.visibleKeys.map(key => ({
        key,
        icon: (META_BY_KEY[key] || {}).icon || 'fa-exclamation-circle',
        label: (META_BY_KEY[key] || {}).label || key,
        sentence: describeAlert(key, this.alertDetails[key]),
      }));
    },

    /** The panel's patient in Patient Profile's `patientData` shape, for the reused modals. */
    patientData() {
      return profilePatientData(this.patient);
    },

    /** Patient Profile's gates for this patient (src/utils/patientPanel/actionGates.js). */
    actionGates() {
      const customer = this.$store.state.customer || {};
      const user = customer.user || null;
      const getSite = this.$store.getters['customer/getSite'];
      const mask = maskState();
      return resolveActionGates({
        allSettings: keySettings(customer.settings),
        patient: this.patientData,
        site: typeof getSite === 'function' ? getSite(this.panelSiteID) || null : null,
        isSuperAdmin: !!(user && user.Role === 'Super Admin'),
        canViewReviews: this.canViewReviews,
        maskOn: mask.userMask === true || mask.siteMask === true,
      });
    },

    canViewReviews() {
      return typeof this.$can === 'function' && this.$can('view', 'reviews');
    },

    actionList() {
      return buildActions(this.actionGates, {
        canViewReviews: this.canViewReviews,
        txLoading: this.busy.txplans,
        paymentOpening: this.busy.pay,
        followupReady: !!this.panelFollowups,
      });
    },
  },

  watch: {
    isOpen(open) {
      if (open) {
        // Focus the dialog so Escape and screen readers land in it, not on the page behind.
        this.$nextTick(() => { if (this.$refs.panel) this.$refs.panel.focus(); });
        // Silent, delayed: the Send Form/Text/Email lists, so the first click is instant.
        scheduleSendCatalogPrefetch(this.$store, this.panelSiteID);
      }
    },
  },

  mounted() {
    // CAPTURE phase — see onKeydown. Must match the removal below.
    document.addEventListener('keydown', this.onKeydown, true);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown, true);
  },

  methods: {
    close() {
      this.$store.dispatch('patientPanel/close');
    },

    setTab(tab) {
      this.$store.dispatch('patientPanel/setTab', tab);
    },

    retry() {
      this.$store.dispatch('patientPanel/fetchPatient');
    },

    onAction(key) {
      if (this.$refs.actionHost) this.$refs.actionHost.run(key);
    },

    onBusy({ key, busy }) {
      this.busy = { ...this.busy, [key]: busy };
    },

    /**
     * Escape closes the panel — unless it was meant for something open ON TOP of it: a modal, a
     * slide-out, a Tx Presenter overlay or a reason popover. Those close
     * themselves; closing the panel too would throw away the patient the user was working on.
     *
     * ⛔ Listened for in the CAPTURE phase, i.e. BEFORE the overlay sees the key. Found 2026-09-25 by
     * the live dev check: in the bubble phase a real key press let Bootstrap's modal close itself
     * first — the browser runs microtasks between listeners, so Vue had already removed `.show`
     * when the event reached `document` (measured: capture `modal.show=true`, bubble `false`) and
     * the panel closed too. Deciding before anyone reacts is the only reading that can't race.
     */
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      if (document.querySelector(OVERLAYS_ABOVE)) return;
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pp-root {
  // Hanken Grotesk is loaded app-wide; the panel uses it like the page that opens it.
  font-family: $pp-font;
}

.pp-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1034;
  background: rgba(21, 32, 26, 0.32);
}

.pp-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1035;
  width: 90%;
  min-width: 680px;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  background: $pp-bg;
  box-shadow: -24px 0 60px -30px rgba(21, 32, 26, 0.5);
  outline: none;
  animation: pp-slide-in 0.18s ease-out;
}

@keyframes pp-slide-in {
  from { transform: translateX(24px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .pp-panel { animation: none; }
}

.pp-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.pp-bar {
  display: none;
}

// The phone back bar is 46px tall; PatientProfileView pins its tab strip under it.
.pp-panel { --ppv-tabs-top: 46px; }

// Below the design's 1100px line the slide-over takes a little more of the window.
@media (max-width: $pp-stack-max) {
  .pp-panel { width: 94%; }
}

// A narrow tablet or a phone turned sideways: the panel takes the whole width, since 680px of
// panel in a 700px window leaves a sliver of backdrop that is easy to tap by mistake.
@media (max-width: 759.98px) {
  .pp-panel { width: 100%; min-width: 0; }
}

// Phone: full screen with a back bar (the design's phone artboard).
@media (max-width: $pp-phone-max) {
  .pp-panel { box-shadow: none; animation: none; }
  .pp-backdrop { display: none; }

  .pp-bar {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 6px;
    background: $pp-surface;
    border-bottom: 1px solid $pp-line-2;
  }

  .pp-bar__btn {
    flex: 0 0 auto;
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 12px;
    background: none;
    color: $pp-ink-2;
    font-size: 16px;
    cursor: pointer;
  }

  .pp-bar__t {
    flex: 1 1 auto;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    color: $pp-ink;
  }
}
</style>
